import { useState } from "react";
import CopyCode from "../CopyCode/CopyCode";
import Icon from "../Icon";

const ImageComponent = () => {
	const [hoveredImage, setHoveredImage] = useState(null);
	const [filters, setFilters] = useState({
		grayscale: false,
		sepia: false,
		brightness: false,
		blur: false
	});

	const toggleFilter = (filterName) => {
		setFilters(prev => ({
			...prev,
			[filterName]: !prev[filterName]
		}));
	};

	const getFilterClass = () => {
		let classes = '';
		if (filters.grayscale) classes += ' grayscale';
		if (filters.sepia) classes += ' sepia';
		if (filters.brightness) classes += ' brightness-150';
		if (filters.blur) classes += ' blur-sm';
		return classes;
	};

	const placeholderImg = 'https://api.dicebear.com/7.x/avataaars/svg?seed=';

	return (
		<div className="flex flex-col gap-y-10 p-6">
			{/* SECTION 1: IMAGE VARIANTS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="image" className="w-5 h-5" /> Image Variants</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 1: Rounded Image */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Rounded Corners</label>
						<div className="flex items-center justify-center h-48">
							<img 
								src={`${placeholderImg}variant1`}
								alt="Rounded" 
								className="w-32 h-32 rounded-lg object-cover border-2 border-amber-400"
							/>
						</div>
						<CopyCode number_code={1} code={`<img 
  src="image.jpg"
  alt="Rounded" 
  className="w-32 h-32 rounded-lg object-cover border-2 border-amber-400"
/>`} />
					</div>

					{/* Variation 2: Circular Image */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Circular</label>
						<div className="flex items-center justify-center h-48">
							<img 
								src={`${placeholderImg}variant2`}
								alt="Circular" 
								className="w-32 h-32 rounded-full object-cover border-4 border-amber-400"
							/>
						</div>
						<CopyCode number_code={2} code={`<img 
  src="image.jpg"
  alt="Circular" 
  className="w-32 h-32 rounded-full object-cover border-4 border-amber-400"
/>`} />
					</div>

					{/* Variation 3: Bordered Image */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Heavy Border</label>
						<div className="flex items-center justify-center h-48">
							<div className="p-3 bg-gradient-to-r from-amber-500 to-amber-500 rounded-lg">
								<img 
									src={`${placeholderImg}variant3`}
									alt="Bordered" 
									className="w-32 h-32 object-cover rounded-md"
								/>
							</div>
						</div>
						<CopyCode number_code={3} code={`<div className="p-3 bg-gradient-to-r from-amber-500 to-amber-500 rounded-lg">
  <img 
    src="image.jpg"
    alt="Bordered" 
    className="w-32 h-32 object-cover rounded-md"
  />
</div>`} />
					</div>

					{/* Variation 4: Square with Shadow */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">With Shadow</label>
						<div className="flex items-center justify-center h-48">
							<img 
								src={`${placeholderImg}variant4`}
								alt="Shadow" 
								className="w-40 h-40 object-cover rounded-lg shadow-2xl shadow-amber-500/30"
							/>
						</div>
						<CopyCode number_code={4} code={`<img 
  src="image.jpg"
  alt="Shadow" 
  className="w-40 h-40 object-cover rounded-lg shadow-2xl shadow-amber-500/30"
/>`} />
					</div>

					{/* Variation 5: With Overlay */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">With Overlay</label>
						<div className="flex items-center justify-center h-48">
							<div className="relative w-40 h-40">
								<img 
									src={`${placeholderImg}variant5`}
									alt="Overlay" 
									className="w-full h-full object-cover rounded-lg"
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent rounded-lg"></div>
								<span className="absolute bottom-2 left-2 text-white text-sm font-semibold">Image</span>
							</div>
						</div>
						<CopyCode number_code={5} code={`<div className="relative w-40 h-40">
  <img src="image.jpg" className="w-full h-full object-cover rounded-lg" />
  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent rounded-lg"></div>
  <span className="absolute bottom-2 left-2 text-white text-sm font-semibold">Label</span>
</div>`} />
					</div>

					{/* Variation 6: Aspect Ratio */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Aspect Ratio</label>
						<div className="flex items-center justify-center h-48">
							<div className="w-40 aspect-video bg-slate-700 rounded-lg overflow-hidden">
								<img 
									src={`${placeholderImg}variant6`}
									alt="Aspect" 
									className="w-full h-full object-cover"
								/>
							</div>
						</div>
						<CopyCode number_code={6} code={`<div className="w-40 aspect-video rounded-lg overflow-hidden">
  <img 
    src="image.jpg" 
    className="w-full h-full object-cover"
  />
</div>`} />
					</div>

				</div>
			</div>

			{/* SECTION 2: IMAGE EFFECTS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="sparkles" className="w-5 h-5" /> Image Effects</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 7: Hover Zoom */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Hover Zoom</label>
						<div className="flex items-center justify-center h-48 overflow-hidden">
							<img 
								src={`${placeholderImg}variant7`}
								alt="Zoom" 
								className="w-40 h-40 object-cover rounded-lg transition-transform duration-300 hover:scale-110 cursor-pointer"
							/>
						</div>
						<CopyCode number_code={7} code={`<img 
  src="image.jpg"
  className="w-40 h-40 object-cover rounded-lg transition-transform duration-300 hover:scale-110"
/>`} />
					</div>

					{/* Variation 8: Grayscale Toggle */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Interactive Filters</label>
						<div className="flex items-center justify-center h-48">
							<img 
								src={`${placeholderImg}variant8`}
								alt="Filters" 
								className={`w-32 h-32 object-cover rounded-lg transition-all duration-300${getFilterClass()}`}
							/>
						</div>
						<div className="flex gap-2 justify-center mt-2">
							<button 
								onClick={() => toggleFilter('grayscale')}
								className={`px-2 py-1 rounded text-xs font-medium transition-all ${filters.grayscale ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-300'}`}
							>
								B&W
							</button>
							<button 
								onClick={() => toggleFilter('blur')}
								className={`px-2 py-1 rounded text-xs font-medium transition-all ${filters.blur ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-300'}`}
							>
								Blur
							</button>
						</div>
						<CopyCode number_code={8} code={`<img 
  src="image.jpg"
  className={filters.grayscale ? 'grayscale' : ''}
/>
<button onClick={() => toggleFilter('grayscale')}>
  Toggle B&W
</button>`} />
					</div>

					{/* Variation 9: Hover Overlay */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Hover Overlay</label>
						<div className="flex items-center justify-center h-48">
							<div 
								className="relative w-40 h-40 group cursor-pointer"
								onMouseEnter={() => setHoveredImage('var9')}
								onMouseLeave={() => setHoveredImage(null)}
							>
								<img 
									src={`${placeholderImg}variant9`}
									alt="Overlay" 
									className="w-full h-full object-cover rounded-lg"
								/>
								<div className={`absolute inset-0 bg-black rounded-lg transition-opacity duration-300 flex items-center justify-center ${hoveredImage === 'var9' ? 'opacity-60' : 'opacity-0'}`}>
									<button className="px-3 py-1 bg-amber-500 text-white rounded text-sm font-semibold">View</button>
								</div>
							</div>
						</div>
						<CopyCode number_code={9} code={`<div className="relative w-40 h-40 group">
  <img src="image.jpg" className="w-full h-full object-cover rounded-lg" />
  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-60 transition-opacity flex items-center justify-center">
    <button>View</button>
  </div>
</div>`} />
					</div>

					{/* Variation 10: Border Animation */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Animated Border</label>
						<div className="flex items-center justify-center h-48">
							<div className="relative w-40 h-40">
								<div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-400 rounded-lg p-0.5 animate-pulse"></div>
								<img 
									src={`${placeholderImg}variant10`}
									alt="Animated" 
									className="relative w-full h-full object-cover rounded-lg"
								/>
							</div>
						</div>
						<CopyCode number_code={10} code={`<div className="relative w-40 h-40">
  <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-400 rounded-lg p-0.5 animate-pulse"></div>
  <img src="image.jpg" className="relative w-full h-full object-cover rounded-lg" />
</div>`} />
					</div>

					{/* Variation 11: Frame Style */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Frame Style</label>
						<div className="flex items-center justify-center h-48">
							<div className="p-4 bg-white rounded-lg shadow-2xl">
								<img 
									src={`${placeholderImg}variant11`}
									alt="Frame" 
									className="w-32 h-32 object-cover"
								/>
							</div>
						</div>
						<CopyCode number_code={11} code={`<div className="p-4 bg-white rounded-lg shadow-2xl">
  <img 
    src="image.jpg"
    className="w-32 h-32 object-cover"
  />
</div>`} />
					</div>

					{/* Variation 12: Gradient Frame */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Gradient Frame</label>
						<div className="flex items-center justify-center h-48">
							<div className="p-1 bg-gradient-to-br from-amber-400 via-amber-400 to-amber-400 rounded-lg">
								<div className="p-3 bg-slate-900 rounded-md">
									<img 
										src={`${placeholderImg}variant12`}
										alt="Gradient" 
										className="w-32 h-32 object-cover rounded"
									/>
								</div>
							</div>
						</div>
						<CopyCode number_code={12} code={`<div className="p-1 bg-gradient-to-br from-amber-400 via-amber-400 to-amber-400 rounded-lg">
  <div className="p-3 bg-slate-900 rounded-md">
    <img src="image.jpg" className="w-32 h-32 object-cover" />
  </div>
</div>`} />
					</div>

				</div>
			</div>
		</div>
	);
};

export default ImageComponent;



